import { httpServiceClient } from '@/infra/http'
import { makeApiUrl } from '@/main/factories/http/api-url-factory'
import { formatGroupList } from './group-service-utils'
import { formatFilterOptions } from '../collaborator/collaborator-service-utils'
import { FilterOptions } from '../collaborator/collaborator-service-types'
import {
  GroupFilterParams,
  GroupListResponse,
  GroupCreateRequest,
  GroupEditRequest
} from './group-service-types'
import { GroupImportResult } from './group-import-service-types'

/**
 * Group Service
 *
 * IMPORTANT: Uses OLD backoffice endpoints (AS-IS migration from skillhub-web)
 * DO NOT change these endpoints without verifying with backend team
 */
export const createGroupService = (client = httpServiceClient) => ({
  /**
   * Fetch paginated list of groups
   * OLD ENDPOINT: GET /api/user/group/
   * @param _companyId - Not used in old API, kept for interface compatibility
   */
  async fetchList(
    _companyId: string,
    params: GroupFilterParams,
    signal?: AbortSignal
  ): Promise<GroupListResponse> {
    const response = await client<GroupListResponse>({
      method: 'get',
      url: makeApiUrl('/user/group/'),
      params: {
        page: params.page,
        name: params.name || undefined,
        companies: params.companies || undefined,
        typegroups: params.typegroups || undefined
      },
      signal
    })

    return formatGroupList(response.body.data as GroupListResponse)
  },

  /**
   * Fetch filter options (companies and type groups)
   * OLD ENDPOINT: GET /api/management/options (same as collaborators)
   * @param _companyId - Not used in old API, kept for interface compatibility
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async fetchFilterOptions(_companyId: string): Promise<FilterOptions> {
    const response = await client<FilterOptions>({
      method: 'get',
      url: makeApiUrl('/management/options')
    })

    return formatFilterOptions(response.body.data as FilterOptions)
  },

  /**
   * Create a new group
   * OLD ENDPOINT: POST /api/user/group
   * @param _companyId - Not used in old API, kept for interface compatibility
   */
  async createGroup(_companyId: string, data: GroupCreateRequest): Promise<void> {
    await client<void>({
      method: 'post',
      url: makeApiUrl('/user/group'),
      body: {
        name: data.name.trim(),
        company: data.company,
        typegroup: data.typegroup
      }
    })
  },

  /**
   * Edit group (update name)
   * OLD ENDPOINT: POST /api/user/group/review/{groupId}
   * @param _companyId - Not used in old API, kept for interface compatibility
   */
  async editGroup(_companyId: string, groupId: number, data: GroupEditRequest): Promise<void> {
    await client<void>({
      method: 'post',
      url: makeApiUrl(`/user/group/review/${groupId}`),
      body: {
        name: data.name.trim(),
        typegroup: data.typegroup
      }
    })
  },

  /**
   * Import users to group from .xlsx file
   * OLD ENDPOINT: POST /api/user/group/{groupId}/import-users
   * @param _companyId - Not used in old API, kept for interface compatibility
   * @param groupId - Group ID to import users into
   * @param file - .xlsx file with user IDs or emails
   */
  async importUsersToGroup(_companyId: string, groupId: number, file: File) {
    const formData = new FormData()
    formData.append('file', file)

    const response = await client<GroupImportResult>({
      method: 'post',
      url: makeApiUrl(`/user/group/${groupId}/import-users`),
      body: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    return (response.body.data || response.body) as GroupImportResult
  },

  /**
   * Toggle group status (activate/deactivate)
   * OLD ENDPOINT: PUT /api/user/group/activateOrDeactivate/{groupId}/{typegroup}
   * @param _companyId - Not used in old API, kept for interface compatibility
   */
  async toggleGroupStatus(_companyId: string, groupId: number, typegroup: string): Promise<void> {
    await client<void>({
      method: 'put',
      url: makeApiUrl(`/user/group/activateOrDeactivate/${groupId}/${typegroup}`)
    })
  }
})

export const groupService = createGroupService()
